import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../design-system/index.css';
import '../threats-managed/ThreatsManaged.scss';
import './DnsCategories.scss';
import type { BDTheme } from '../../design-system/types';
import { getLanguage } from '../../util/languages';
import { CategoryKpis } from './CategoryKpis';
import { ByOperatorTab } from './ByOperatorTab';
import { CategoryDrilldown } from './CategoryDrilldown';
import { CategoryList } from './CategoryList';
import { CategoryTimeline } from './CategoryTimeline';
import { fetchSummary } from './api';
import type { CategorySummary, Period } from './types';

const PERIODS: Period[] = ['1d', '7d', '30d'];
const DEFAULT_SELECTED = 8;

type Option = { id: number; label: string };

export const DnsCategories = (props: { darkMode?: boolean }) => {
  const { operatorId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const theme: BDTheme = props.darkMode ? 'dark' : 'light';
  const locale = getLanguage(i18n.language).locale;

  const [period, setPeriod] = useState<Period>('1d');
  const [cpeId, setCpeId] = useState<number>();
  const [devId, setDevId] = useState<number>();
  const [cpes, setCpes] = useState<Option[]>([]);
  const [devices, setDevices] = useState<Option[]>([]);
  const [summary, setSummary] = useState<CategorySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string>();
  const [isMaster, setIsMaster] = useState(false);
  const [tab, setTab] = useState<'overview' | 'operators'>('overview');

  // The By operator tab is only for master operators
  useEffect(() => {
    let cancelled = false;
    const email = localStorage.getItem('email');
    setTab('overview');
    if (!email) {
      setIsMaster(false);
      return undefined;
    }
    axios
      .get('/v2/op/master-operator/master', { params: { email } })
      .then((res) => !cancelled && setIsMaster(Boolean(res.data.masterControl)))
      .catch(() => !cancelled && setIsMaster(false));
    return () => {
      cancelled = true;
    };
  }, [operatorId]);

  // CPE / device pickers
  useEffect(() => {
    let cancelled = false;
    setCpeId(undefined);
    setDevId(undefined);
    axios
      .get(`/v2/op/account/${operatorId}/list-accounts`, { params: { size: 500 } })
      .then((res) => !cancelled && setCpes((res.data.data || []).map((r: any) => ({ id: r['CPE ID'], label: `#${r['CPE ID']} ${r['Registrant'] || ''}` }))))
      .catch(() => !cancelled && setCpes([]));
    axios
      .get(`/v2/op/device/${operatorId}/list-devices`, { params: { size: 500 } })
      .then((res) => !cancelled && setDevices((res.data.data || []).map((r: any) => ({ id: r['ID'], label: `#${r['ID']} ${r['Name'] || ''}` }))))
      .catch(() => !cancelled && setDevices([]));
    return () => {
      cancelled = true;
    };
  }, [operatorId]);

  // Summary: refetch on any scope change and reset the chart selection to the top categories
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchSummary(operatorId, { l: period, cpeId, devId })
      .then((data) => {
        if (cancelled) return;
        setSummary(data);
        setSelectedIds(data.categories.filter((c) => c.id !== 'uncategorized').slice(0, DEFAULT_SELECTED).map((c) => c.id));
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [operatorId, period, cpeId, devId]);

  const toggle = (id: string) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const activeCategory = summary?.categories.find((c) => c.id === activeId);
  const empty = useMemo(() => !!summary && summary.categories.length === 0, [summary]);

  return (
    <div className="bd tm-page" data-theme={theme} data-accent="mint">
      <div className="tm-wrap">
        <header className="tm-hd">
          <span className="tm-eyebrow">{t('dnsCategories.eyebrow')}</span>
          <h1 className="bd-h1 tm-title">{t('dnsCategories.title')}</h1>
          <span className="bd-body">{t('dnsCategories.subtitle')}</span>
        </header>

        {summary && <CategoryKpis summary={summary} />}

        <div className="dc-toolbar">
          {isMaster ? (
            <div className="bd-tabs" role="tablist" aria-label={t('dnsCategories.tabs.ariaLabel')}>
              {(['overview', 'operators'] as const).map((key) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>
                  {t(`dnsCategories.tabs.${key}`)}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}
          <div className="bd-segmented tm-range" role="group" aria-label={t('dnsCategories.range.ariaLabel')}>
            {PERIODS.map((p) => (
              <button key={p} type="button" className={period === p ? 'active' : ''} aria-pressed={period === p} onClick={() => setPeriod(p)}>
                {t(`dnsCategories.range.${p}`)}
              </button>
            ))}
          </div>
        </div>

        {isMaster && tab === 'operators' && <ByOperatorTab operatorId={operatorId} period={period} darkMode={props.darkMode} />}

        {tab === 'overview' && (
        <div className="tm-grid">
          <aside className="bd-card tm-panel">
            <div className="tm-filters">
              <div className="bd-select">
                <select aria-label={t('dnsCategories.filters.cpe')} value={cpeId ?? ''} onChange={(e) => { setCpeId(e.target.value ? Number(e.target.value) : undefined); setDevId(undefined); }}>
                  <option value="">{t('dnsCategories.filters.allCpes')}</option>
                  {cpes.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              <div className="bd-select">
                <select aria-label={t('dnsCategories.filters.device')} value={devId ?? ''} onChange={(e) => { setDevId(e.target.value ? Number(e.target.value) : undefined); setCpeId(undefined); }}>
                  <option value="">{t('dnsCategories.filters.allDevices')}</option>
                  {devices.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {loading && !summary && <div className="dc-state bd-meta">{t('dnsCategories.loading')}</div>}
            {error && <div className="dc-state bd-meta" role="alert">{t('dnsCategories.error')}</div>}
            {empty && !error && (
              <div className="dc-state">
                <h3 className="bd-h3">{t('dnsCategories.empty.title')}</h3>
                <span className="bd-meta">{t('dnsCategories.empty.body')}</span>
              </div>
            )}
            {summary && !empty && !error && (
              <CategoryList
                categories={summary.categories}
                selectedIds={selectedIds}
                activeId={activeId}
                locale={locale}
                onToggle={toggle}
                onOpen={(id) => setActiveId((prev) => (prev === id ? undefined : id))}
              />
            )}
          </aside>

          {summary && !empty && !error && (
            <CategoryTimeline
              operatorId={operatorId}
              period={period}
              cpeId={cpeId}
              devId={devId}
              categoryIds={selectedIds}
              darkMode={props.darkMode}
            />
          )}
        </div>
        )}

        {tab === 'overview' && activeCategory && (
          <CategoryDrilldown
            operatorId={operatorId}
            category={{ id: activeCategory.id, name: activeCategory.name }}
            period={period}
            cpeId={cpeId}
            devId={devId}
            onClose={() => setActiveId(undefined)}
          />
        )}
      </div>
    </div>
  );
};
